import React, { useCallback, useMemo } from 'react';
import {
  ModelScopeFactory,
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { PresentationalChat } from '@cotera/client/v0/chat/presentational-chat';
import { HijackerQueueModel } from '@cotera/client/v0/chat/hijacker-queue.model';
import type { ChatModel } from '@cotera/client/v0/chat/chat.model';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { getV1ToolRenderer } from '../components/v1-tool-renderers';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import { WorkflowRunAgentConversationModel } from './workflow-run-agent-conversation.model';

const SUB_AGENT_EXCLUDED_MESSAGE_FOOTERS = ['thinking'] as const;

const createReadonlyChatModel = (
  conversationId: string,
  status: 'ready' | 'streaming' | 'submitted' | 'error'
): Pick<
  ChatModel,
  | 'lastMessageId'
  | 'status'
  | 'addToolResult'
  | 'id'
  | 'model'
  | 'inputWatchable'
> => ({
  id: conversationId,
  get lastMessageId() {
    return undefined;
  },
  get status() {
    return status;
  },
  get model() {
    return '';
  },
  inputWatchable: Watchable.fromValue(''),
  addToolResult(_toolCallId: string, _result: unknown) {
    // readonly
  },
});

const WorkflowRunReadonlyConversationBody: React.FC<{
  chatId: string;
}> = ({ chatId }) => {
  const conversationModel = useInScopeModel(WorkflowRunAgentConversationModel);

  const messages = useWatchableValue(conversationModel.messagesWatchable);
  const status = useWatchableValue(conversationModel.statusWatchable);
  const chatModel = useMemo(
    () => createReadonlyChatModel(chatId, status),
    [chatId, status]
  );
  const customToolRenderers = useMemo(
    () => (toolKey: string) => getV1ToolRenderer(toolKey),
    []
  );

  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
      onWheel={(event) => {
        event.stopPropagation();
      }}
    >
      <PresentationalChat
        useNativeConversationScroll
        excludedMessageFooterIds={SUB_AGENT_EXCLUDED_MESSAGE_FOOTERS}
        status={
          status as 'ready' | 'streaming' | 'submitted' | 'error' | 'loading'
        }
        messages={messages}
        onSubmit={() => {
          /* readonly */
        }}
        readonly={true}
        hideInput={true}
        suppressHijackers={true}
        chatModel={chatModel}
        customToolRenderers={customToolRenderers}
        conversationId={chatId}
        messageActionsConfig={{ showReplay: false, showEdit: false }}
      />
    </div>
  );
};

/** Full-page readonly conversation view for a workflow agent run step. */
export const WorkflowRunReadonlyConversation: React.FC<{
  chatId: string;
  isActive: boolean;
}> = ({ chatId, isActive }) => {
  const client = useTenantedClient();
  const watchablesFactory = useWatchablesFactory();
  const createModels = useCallback(() => {
    const conversationModel = new WorkflowRunAgentConversationModel({
      chatId,
      isActive,
      client: client.resource.chat,
      watchablesFactory,
    });
    return [
      conversationModel,
      new HijackerQueueModel(chatId, conversationModel.messagesWatchable),
    ];
  }, [chatId, isActive, client.resource.chat, watchablesFactory]);

  return (
    <ModelScopeFactory createModels={createModels} deps={[chatId, isActive]}>
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <WorkflowRunReadonlyConversationBody chatId={chatId} />
      </div>
    </ModelScopeFactory>
  );
};
